/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSyncedAgentUsersRequest,
  ListSyncedAgentUsersSecurity,
} from "../models/operations/listsyncedagentusers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSyncedAgentUsersQuery,
  prefetchSyncedAgentUsers,
  queryKeySyncedAgentUsers,
  SyncedAgentUsersQueryData,
} from "./syncedAgentUsers.core.js";
export {
  buildSyncedAgentUsersQuery,
  prefetchSyncedAgentUsers,
  queryKeySyncedAgentUsers,
  type SyncedAgentUsersQueryData,
};

export type SyncedAgentUsersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSyncedUsers agent
 *
 * @remarks
 * List users in the current organization who are actively running the Speakeasy device agent, attributed by the email each agent reports on sync. Dashboard-only; requires an org admin session.
 */
export function useSyncedAgentUsers(
  request?: ListSyncedAgentUsersRequest | undefined,
  security?: ListSyncedAgentUsersSecurity | undefined,
  options?: QueryHookOptions<
    SyncedAgentUsersQueryData,
    SyncedAgentUsersQueryError
  >,
): UseQueryResult<SyncedAgentUsersQueryData, SyncedAgentUsersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSyncedAgentUsersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSyncedUsers agent
 *
 * @remarks
 * List users in the current organization who are actively running the Speakeasy device agent, attributed by the email each agent reports on sync. Dashboard-only; requires an org admin session.
 */
export function useSyncedAgentUsersSuspense(
  request?: ListSyncedAgentUsersRequest | undefined,
  security?: ListSyncedAgentUsersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SyncedAgentUsersQueryData,
    SyncedAgentUsersQueryError
  >,
): UseSuspenseQueryResult<
  SyncedAgentUsersQueryData,
  SyncedAgentUsersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSyncedAgentUsersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSyncedAgentUsersData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: SyncedAgentUsersQueryData,
): SyncedAgentUsersQueryData | undefined {
  const key = queryKeySyncedAgentUsers(...queryKeyBase);

  return client.setQueryData<SyncedAgentUsersQueryData>(key, data);
}

export function invalidateSyncedAgentUsers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "agent", "listSyncedUsers", ...queryKeyBase],
  });
}

export function invalidateAllSyncedAgentUsers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "agent", "listSyncedUsers"],
  });
}
